import { useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ApiClientError } from '@/lib/api-client'
import { artistFetch } from '@/lib/artist-api'
import { PageHeader } from '@/components/PageHeader'
import { AsyncBoundary } from '@/components/AsyncBoundary'
import { IntelVenueImportModal } from '@/components/IntelVenueImportModal'

// ─── Types ─────────────��────────────────────────────���────────────────────────

interface VenueConfiguration {
  id: string
  name: string
  capacityStanding: number | null
  capacitySeated: number | null
  stageWidthM: number | null
  stageDepthM: number | null
  stageHeightM: number | null
  sightlineNotes: string | null
  productionNotes: string | null
  isDefault: boolean
}

interface VenueEquipmentItem {
  id: string
  name: string
  category: string
  specification: string | null
  quantity: number
  hirePricePence: number | null
  isMandatory: boolean
  isProhibited: boolean
  notes: string | null
}

interface VenueAvailability {
  id: string
  date: string
  status: string
  notes: string | null
}

interface VenueSummary {
  id: string
  tier: 'ghost' | 'claimed' | 'structured' | 'premium'
  name: string
  slug: string
  city: string
  country: string
  capacityMax: number | null
  venueType: string | null
  website: string | null
  bookingEmail: string | null
  hasStructuredData: boolean
  intelVenueId: string | null
  configurations: VenueConfiguration[]
  _count: { configurations: number; equipment: number }
}

interface VenueDetail extends VenueSummary {
  address: string | null
  lat: number | null
  lng: number | null
  bookingContactName: string | null
  bookingPhone: string | null
  notes: string | null
  acceptsOvationTicketing: boolean
  equipment: VenueEquipmentItem[]
  availability: VenueAvailability[]
}

interface SearchResult {
  data: VenueSummary[]
  meta: { page: number; limit: number; total: number }
}

// ─── Tier badge ─────��────────────────────────────────────────────────────────

const TIER_STYLES: Record<string, string> = {
  ghost: 'bg-gray-100 text-gray-600',
  claimed: 'bg-blue-100 text-blue-700',
  structured: 'bg-emerald-100 text-emerald-700',
  premium: 'bg-amber-100 text-amber-700',
}

function TierBadge({ tier }: { tier: string }) {
  return (
    <span
      className={`inline-block rounded px-2 py-0.5 text-xs font-medium capitalize ${TIER_STYLES[tier] ?? 'bg-gray-100 text-gray-600'}`}
    >
      {tier}
    </span>
  )
}

// ─── Data completeness indicator ───���─────────────────────────────────────────

function CompletenessBar({ venue }: { venue: VenueSummary }) {
  let score = 0
  if (venue.capacityMax) score += 20
  if (venue.website) score += 15
  if (venue.bookingEmail) score += 20
  if (venue._count.configurations > 0) score += 25
  if (venue._count.equipment > 0) score += 20
  score = Math.min(score, 100)

  const color =
    score >= 80
      ? 'bg-emerald-500'
      : score >= 50
        ? 'bg-amber-500'
        : 'bg-gray-400'

  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-20 overflow-hidden rounded-full bg-gray-200">
        <div className={`h-full ${color} transition-all`} style={{ width: `${score}%` }} />
      </div>
      <span className="text-xs text-ovation-muted">{score}%</span>
    </div>
  )
}

// ─── Venue card ────────���─────────────────────────────────────────────────────

function VenueCard({
  venue,
  isExpanded,
  onToggle,
  onInquire,
}: {
  venue: VenueSummary
  isExpanded: boolean
  onToggle: () => void
  onInquire: () => void
}) {
  return (
    <div className="rounded-lg border border-ovation-ink/10 bg-white transition-shadow hover:shadow-md">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-start justify-between p-4 text-left"
      >
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="font-display text-lg">{venue.name}</h3>
            <TierBadge tier={venue.tier} />
          </div>
          <div className="mt-1 text-sm text-ovation-muted">
            {venue.city}, {venue.country}
            {venue.capacityMax && (
              <span className="ml-2">
                &middot; {venue.capacityMax.toLocaleString()} capacity
              </span>
            )}
            {venue.venueType && (
              <span className="ml-2">&middot; {venue.venueType}</span>
            )}
          </div>
          <div className="mt-2">
            <CompletenessBar venue={venue} />
          </div>
        </div>
        <span className="ml-4 text-ovation-muted">{isExpanded ? '-' : '+'}</span>
      </button>

      {isExpanded && <VenueExpandedDetail venueId={venue.id} onInquire={onInquire} />}
    </div>
  )
}

// ─── Expanded detail panel (lazy-loads full venue data) ──────────────────────

function VenueExpandedDetail({
  venueId,
  onInquire,
}: {
  venueId: string
  onInquire: () => void
}) {
  const { data: venue, isLoading, error } = useQuery<VenueDetail, ApiClientError>({
    queryKey: ['venue-detail', venueId],
    queryFn: () => artistFetch<VenueDetail>(`/venues/${venueId}`),
  })

  if (isLoading) {
    return <div className="px-4 pb-4 text-sm text-ovation-muted">Loading details...</div>
  }
  if (error) {
    return <div className="px-4 pb-4 text-sm text-red-600">{error.message}</div>
  }
  if (!venue) return null

  return (
    <div className="border-t border-ovation-ink/5 px-4 pb-4 pt-3">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {/* Contact */}
        <div>
          <h4 className="mb-1 text-xs font-semibold uppercase tracking-wider text-ovation-muted">
            Contact
          </h4>
          <div className="space-y-1 text-sm">
            {venue.bookingContactName && <div>{venue.bookingContactName}</div>}
            {venue.bookingEmail && (
              <div>
                <a href={`mailto:${venue.bookingEmail}`} className="text-blue-600 hover:underline">
                  {venue.bookingEmail}
                </a>
              </div>
            )}
            {venue.bookingPhone && <div>{venue.bookingPhone}</div>}
            {venue.website && (
              <div>
                <a
                  href={venue.website}
                  target="_blank"
                  rel="noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  Website
                </a>
              </div>
            )}
            {venue.address && <div className="text-ovation-muted">{venue.address}</div>}
          </div>
        </div>

        {/* Configurations */}
        <div>
          <h4 className="mb-1 text-xs font-semibold uppercase tracking-wider text-ovation-muted">
            Configurations ({venue.configurations.length})
          </h4>
          {venue.configurations.length === 0 ? (
            <p className="text-sm text-ovation-muted">No configurations listed</p>
          ) : (
            <div className="space-y-2">
              {venue.configurations.map((c) => (
                <div key={c.id} className="rounded bg-ovation-ink/5 p-2 text-sm">
                  <div className="font-medium">
                    {c.name}
                    {c.isDefault && (
                      <span className="ml-1 text-xs text-ovation-muted">(default)</span>
                    )}
                  </div>
                  <div className="text-ovation-muted">
                    {c.capacitySeated && `${c.capacitySeated} seated`}
                    {c.capacitySeated && c.capacityStanding && ' / '}
                    {c.capacityStanding && `${c.capacityStanding} standing`}
                  </div>
                  {(c.stageWidthM || c.stageDepthM) && (
                    <div className="text-ovation-muted">
                      Stage: {c.stageWidthM ?? '?'}m x {c.stageDepthM ?? '?'}m
                      {c.stageHeightM && ` x ${c.stageHeightM}m`}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Equipment */}
        <div>
          <h4 className="mb-1 text-xs font-semibold uppercase tracking-wider text-ovation-muted">
            Equipment ({venue.equipment.length})
          </h4>
          {venue.equipment.length === 0 ? (
            <p className="text-sm text-ovation-muted">No equipment listed</p>
          ) : (
            <div className="max-h-40 space-y-1 overflow-y-auto text-sm">
              {venue.equipment.map((e) => (
                <div key={e.id} className="flex items-center gap-2">
                  <span className="flex-1 truncate">{e.name}</span>
                  {e.isMandatory && (
                    <span className="rounded bg-red-100 px-1 text-xs text-red-700">
                      mandatory
                    </span>
                  )}
                  {e.isProhibited && (
                    <span className="rounded bg-gray-100 px-1 text-xs text-gray-700">
                      prohibited
                    </span>
                  )}
                  {e.hirePricePence != null && (
                    <span className="text-xs text-ovation-muted">
                      {(e.hirePricePence / 100).toFixed(2)}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Availability calendar (simple list) */}
      {venue.availability.length > 0 && (
        <div className="mt-4">
          <h4 className="mb-1 text-xs font-semibold uppercase tracking-wider text-ovation-muted">
            Upcoming Availability
          </h4>
          <div className="flex flex-wrap gap-1">
            {venue.availability.slice(0, 30).map((a) => {
              const bg =
                a.status === 'available'
                  ? 'bg-emerald-100 text-emerald-800'
                  : a.status === 'enquiry_hold'
                    ? 'bg-amber-100 text-amber-800'
                    : 'bg-red-100 text-red-800'
              return (
                <span key={a.id} className={`rounded px-1.5 py-0.5 text-xs ${bg}`} title={a.status}>
                  {new Date(a.date).toLocaleDateString('en-GB', {
                    day: 'numeric',
                    month: 'short',
                  })}
                </span>
              )
            })}
          </div>
        </div>
      )}

      {/* Notes */}
      {venue.notes && (
        <div className="mt-3">
          <h4 className="mb-1 text-xs font-semibold uppercase tracking-wider text-ovation-muted">
            Notes
          </h4>
          <p className="whitespace-pre-line text-sm">{venue.notes}</p>
        </div>
      )}

      {/* Actions */}
      <div className="mt-4 flex gap-2">
        {venue.bookingEmail && (
          <button
            type="button"
            onClick={onInquire}
            className="rounded-md bg-ovation-ink px-4 py-2 text-sm text-ovation-paper transition-colors hover:bg-ovation-ink/90"
          >
            Send inquiry
          </button>
        )}
        {venue.acceptsOvationTicketing && (
          <span className="flex items-center rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
            Ovation Ticketing available
          </span>
        )}
      </div>
    </div>
  )
}

// ─── Inquiry modal ───────────────────────────────────────────────────────────

function InquiryModal({
  venueId,
  venueName,
  onClose,
}: {
  venueId: string
  venueName: string
  onClose: () => void
}) {
  const [message, setMessage] = useState('')
  const [eventDate, setEventDate] = useState('')
  const [capacity, setCapacity] = useState('')

  const mutation = useMutation({
    mutationFn: () =>
      artistFetch(`/venues/${venueId}/inquire`, {
        method: 'POST',
        body: {
          message,
          ...(eventDate && { eventDate }),
          ...(capacity && { expectedCapacity: Number(capacity) }),
        },
      }),
    onSuccess: () => onClose(),
  })

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl">
        <h3 className="font-display text-xl">Send inquiry to {venueName}</h3>
        <div className="mt-4 space-y-3">
          <div>
            <label className="mb-1 block text-sm text-ovation-muted">Message</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              className="w-full rounded-md border border-ovation-ink/20 px-3 py-2 text-sm focus:border-ovation-ink focus:outline-none"
              placeholder="Describe your event and requirements..."
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-sm text-ovation-muted">Event date</label>
              <input
                type="date"
                value={eventDate}
                onChange={(e) => setEventDate(e.target.value)}
                className="w-full rounded-md border border-ovation-ink/20 px-3 py-2 text-sm focus:border-ovation-ink focus:outline-none"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm text-ovation-muted">Expected capacity</label>
              <input
                type="number"
                value={capacity}
                onChange={(e) => setCapacity(e.target.value)}
                className="w-full rounded-md border border-ovation-ink/20 px-3 py-2 text-sm focus:border-ovation-ink focus:outline-none"
                placeholder="e.g. 500"
              />
            </div>
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-ovation-ink/20 px-4 py-2 text-sm hover:bg-ovation-ink/5"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => mutation.mutate()}
            disabled={!message.trim() || mutation.isPending}
            className="rounded-md bg-ovation-ink px-4 py-2 text-sm text-ovation-paper transition-colors hover:bg-ovation-ink/90 disabled:opacity-50"
          >
            {mutation.isPending ? 'Sending...' : 'Send inquiry'}
          </button>
        </div>
        {mutation.isError && (
          <p className="mt-2 text-sm text-red-600">
            {(mutation.error as any)?.message ?? 'Failed to send inquiry'}
          </p>
        )}
      </div>
    </div>
  )
}

// ��── Main page ─────────��──────────────────────────────���──────────────────────

export function VenuesPage() {
  const queryClient = useQueryClient()
  const [city, setCity] = useState('')
  const [capacityMin, setCapacityMin] = useState('')
  const [capacityMax, setCapacityMax] = useState('')
  const [venueType, setVenueType] = useState('')
  const [searchTrigger, setSearchTrigger] = useState(0)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [inquiryTarget, setInquiryTarget] = useState<{ id: string; name: string } | null>(null)
  const [page, setPage] = useState(1)
  const [importModalOpen, setImportModalOpen] = useState(false)

  const queryParams = new URLSearchParams()
  if (city) queryParams.set('city', city)
  if (capacityMin) queryParams.set('capacityMin', capacityMin)
  if (capacityMax) queryParams.set('capacityMax', capacityMax)
  if (venueType) queryParams.set('venueType', venueType)
  queryParams.set('page', String(page))
  queryParams.set('limit', '25')

  const {
    data: result,
    isLoading,
    error,
  } = useQuery<SearchResult, ApiClientError>({
    queryKey: ['venues', city, capacityMin, capacityMax, venueType, page, searchTrigger],
    queryFn: () =>
      artistFetch<SearchResult>(`/venues/search?${queryParams.toString()}`).then(
        (r) => r as unknown as SearchResult,
      ),
    enabled: searchTrigger > 0,
  })

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setPage(1)
    setSearchTrigger((n) => n + 1)
  }

  // The search endpoint wraps results in { data, meta } via the route.
  // artistFetch already unwraps the outer `data` envelope, so we get
  // the array directly OR the full { data, meta } depending on how it's returned.
  // Handle both shapes defensively.
  const venues: VenueSummary[] = Array.isArray(result)
    ? result
    : (result as any)?.data ?? (result as any)?.items ?? []
  const meta = Array.isArray(result) ? null : (result as any)?.meta

  // Set of intelVenueIds already imported into this tenant's venue list,
  // derived from the current results page. The modal uses this to render
  // "Already imported" for matching intel rows. Newly imported venues
  // (after the modal triggers onImported → refetch) show up here too.
  const alreadyImportedIntelIds = useMemo(
    () =>
      new Set(
        venues
          .map((v) => v.intelVenueId)
          .filter((id): id is string => id != null),
      ),
    [venues],
  )

  return (
    <div>
      <PageHeader
        title="Venues"
        subtitle="Search venues by city, capacity, and type. Click a venue to see configurations, equipment, and availability."
      />

      {/* Search form */}
      <form onSubmit={handleSearch} className="mb-6 flex flex-wrap items-end gap-3">
        <div>
          <label className="mb-1 block text-xs text-ovation-muted">City</label>
          <input
            type="text"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            className="rounded-md border border-ovation-ink/20 px-3 py-2 text-sm focus:border-ovation-ink focus:outline-none"
            placeholder="e.g. London"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs text-ovation-muted">Capacity min</label>
          <input
            type="number"
            value={capacityMin}
            onChange={(e) => setCapacityMin(e.target.value)}
            className="w-24 rounded-md border border-ovation-ink/20 px-3 py-2 text-sm focus:border-ovation-ink focus:outline-none"
            placeholder="0"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs text-ovation-muted">Capacity max</label>
          <input
            type="number"
            value={capacityMax}
            onChange={(e) => setCapacityMax(e.target.value)}
            className="w-24 rounded-md border border-ovation-ink/20 px-3 py-2 text-sm focus:border-ovation-ink focus:outline-none"
            placeholder="any"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs text-ovation-muted">Venue type</label>
          <select
            value={venueType}
            onChange={(e) => setVenueType(e.target.value)}
            className="rounded-md border border-ovation-ink/20 px-3 py-2 text-sm focus:border-ovation-ink focus:outline-none"
          >
            <option value="">Any type</option>
            <option value="arena">Arena</option>
            <option value="theatre">Theatre</option>
            <option value="concert_hall">Concert hall</option>
            <option value="club">Club</option>
            <option value="stadium">Stadium</option>
            <option value="festival_ground">Festival ground</option>
            <option value="casino">Casino</option>
            <option value="other">Other</option>
          </select>
        </div>
        <button
          type="submit"
          className="rounded-md bg-ovation-ink px-5 py-2 text-sm text-ovation-paper transition-colors hover:bg-ovation-ink/90"
        >
          Search
        </button>
        <button
          type="button"
          onClick={() => setImportModalOpen(true)}
          className="rounded-md border border-ovation-ink/20 px-4 py-2 text-sm transition-colors hover:bg-ovation-ink/5"
        >
          Import from Research
        </button>
      </form>

      {/* Results */}
      {searchTrigger === 0 ? (
        <p className="text-sm text-ovation-muted">
          Enter search criteria and click Search to find venues.
        </p>
      ) : (
        <AsyncBoundary isLoading={isLoading} error={error ?? null}>
          {venues.length === 0 ? (
            <p className="text-sm text-ovation-muted">
              No venues found matching your criteria.
            </p>
          ) : (
            <>
              {meta && (
                <p className="mb-3 text-sm text-ovation-muted">
                  {meta.total} venue{meta.total === 1 ? '' : 's'} found
                </p>
              )}
              <div className="space-y-3">
                {venues.map((v) => (
                  <VenueCard
                    key={v.id}
                    venue={v}
                    isExpanded={expandedId === v.id}
                    onToggle={() =>
                      setExpandedId(expandedId === v.id ? null : v.id)
                    }
                    onInquire={() =>
                      setInquiryTarget({ id: v.id, name: v.name })
                    }
                  />
                ))}
              </div>

              {/* Pagination */}
              {meta && meta.total > meta.limit && (
                <div className="mt-4 flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    className="rounded border border-ovation-ink/20 px-3 py-1 text-sm disabled:opacity-40"
                  >
                    Prev
                  </button>
                  <span className="text-sm text-ovation-muted">
                    Page {page} of {Math.ceil(meta.total / meta.limit)}
                  </span>
                  <button
                    type="button"
                    onClick={() => setPage((p) => p + 1)}
                    disabled={page >= Math.ceil(meta.total / meta.limit)}
                    className="rounded border border-ovation-ink/20 px-3 py-1 text-sm disabled:opacity-40"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </AsyncBoundary>
      )}

      {/* Inquiry modal */}
      {inquiryTarget && (
        <InquiryModal
          venueId={inquiryTarget.id}
          venueName={inquiryTarget.name}
          onClose={() => setInquiryTarget(null)}
        />
      )}

      {/* Import-from-intel modal */}
      <IntelVenueImportModal
        open={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        onImported={() => {
          // Invalidate the current venues query so newly imported venues
          // show up in the tenant list next time the user re-runs a search.
          queryClient.invalidateQueries({ queryKey: ['venues'] })
        }}
        alreadyImportedIntelIds={alreadyImportedIntelIds}
      />
    </div>
  )
}
